import { createContext, useContext, type ReactNode } from 'react';

/**
 * Publishes the dashboard's locally-held selected year to descendant charts, so exports can
 * label their "năm" filter without prop-drilling through every ChartCard call site.
 *
 * Năm only. Khoa is global and comes from `hooks/useGlobalFacultyFilter.ts` — the faculty
 * filter lives in the app-wide side menu, so it applies to charts this provider never wraps.
 */
const ChartYearContext = createContext<number | null | undefined>(undefined);

export interface ChartYearProviderProps {
	year: number | null;
	children: ReactNode;
}

export function ChartYearProvider({ year, children }: ChartYearProviderProps) {
	return <ChartYearContext.Provider value={year}>{children}</ChartYearContext.Provider>;
}

/**
 * Returns the dashboard's selected year, or `undefined` when rendered outside a
 * `ChartYearProvider`. Never throws — components that may render both inside and outside the
 * dashboard (and tests that mount a chart in isolation) must keep compiling and passing.
 */
export function useChartYearContext(): number | null | undefined {
	return useContext(ChartYearContext);
}
